import { Alert, Box, FormControl, InputLabel, MenuItem } from "@mui/material";
import { UNGROUPED_CELL_ID, cellValueFromGQL, isNotUndefined, numberCellValueFromGQL } from "Backend";
import PSelect from "components/PSelect";
import { FormFieldContext, ValueGroupsByDate } from "contexts/FormFieldContext";
import { CellType, Cell } from "generated/graphql";
import { HighchartsReact } from "highcharts-react-official";
import Highcharts, { SeriesColumnOptions, SeriesLineOptions } from "highcharts/highstock";
import { useContext, useMemo } from "react";

interface DateByNameCell {
  name: string;
  type: SeriesColumnOptions["type"] | SeriesLineOptions["type"];
  data: [number, number][];
}

function numberSeriesForCellValues(
  valueGroupsByDate: ValueGroupsByDate[],
  groupByCellId: number,
  cell: Cell
): DateByNameCell[] {
  const data = valueGroupsByDate.map((sd) => {
    const cellValuesToUse = sd.valueGroups
      .map((vg) => vg.values.find((v) => v.cellId === groupByCellId)?.value)
      .filter((cellValue) => isNotUndefined(numberCellValueFromGQL(cellValue)));
    const firstCellValue = cellValuesToUse[0];
    if (firstCellValue === undefined) {
      return [];
    }
    return [sd.date.getTime(), numberCellValueFromGQL(firstCellValue)];
  });

  if (data[0]?.length === 0) {
    return [];
  }
  const series: DateByNameCell[] = [
    {
      name: cell.name,
      type: "line",
      data: data as [number, number][],
    },
  ];
  return series;
}

function seriesForCellValues(valueGroupsByDate: ValueGroupsByDate[], groupByCellId: number): DateByNameCell[] {
  const series: DateByNameCell[] = [];
  valueGroupsByDate.forEach((sd) => {
    sd.valueGroups.forEach((vg) => {
      const cellValue = cellValueFromGQL(vg.values.find((v) => v.cellId === groupByCellId)?.value)?.toString();
      if (cellValue !== undefined) {
        const found = series.find((d) => d.name === cellValue);
        if (found === undefined) {
          series.push({
            name: cellValue,
            type: "column",
            data: [[sd.date.getTime(), 1]],
          });
        } else {
          const foundTime = found.data.find((d) => d[0] === sd.date.getTime());
          if (foundTime === undefined) {
            found.data.push([sd.date.getTime(), 1]);
          } else {
            foundTime[1]++;
          }
        }
      }
    });
  });
  return series;
}

export default function EntryTimeline() {
  const ffContext = useContext(FormFieldContext);
  const cellSchema = ffContext.formField.cells.find((cell) => cell.id === ffContext.groupByCellIdHandle[0]);
  const series: DateByNameCell[] = useMemo(() => {
    if (ffContext.groupByCellIdHandle[0] === undefined) return [];
    if (cellSchema?.type === CellType.Integer || cellSchema?.type === CellType.Float) {
      return numberSeriesForCellValues(ffContext.valueGroupsByDate, ffContext.groupByCellIdHandle[0], cellSchema);
    } else {
      return seriesForCellValues(ffContext.valueGroupsByDate, ffContext.groupByCellIdHandle[0]);
    }
  }, [ffContext.valueGroupsByDate, ffContext.groupByCellIdHandle[0], cellSchema]);

  const options: Highcharts.Options = {
    credits: {
      enabled: false,
    },
    tooltip: {
      xDateFormat: "%m/%d/%Y",
    },
    title: {
      text: undefined,
    },
    time: {
      useUTC: false,
    },
    legend: {
      enabled: true,
    },
    series,
    plotOptions: {
      column: {
        pointWidth: 8,
      },
    },
    xAxis: {
      // tickInterval: 24 * 3600 * 1000 * 365,
      labels: {
        style: {
          fontFamily: "Inter,arial,sans-serif",
          fontSize: "10px",
        },
      },
      type: "datetime",
      dateTimeLabelFormats: {
        minute: "%l:%M %p",
        second: "%l:%M:%S %p",
        millisecond: "%H:%M:%S %p",
      },
    },
    yAxis: {
      labels: {
        style: {
          fontFamily: "Inter,arial,sans-serif",
          fontSize: "10px",
        },
      },
      title: {
        text: "Count",
      },
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <div>
        <FormControl sx={{ mb: 2, mt: 1 }}>
          <InputLabel>Group by</InputLabel>
          <PSelect
            sx={{
              minWidth: 200,
            }}
            label="Group by"
            value={ffContext.groupByCellIdHandle[0]}
            onChange={(e) => ffContext.groupByCellIdHandle[1](e.target.value as number)}>
            <MenuItem value={UNGROUPED_CELL_ID}>None</MenuItem>
            {ffContext.validGroupByCells.map((cell, index) => (
              <MenuItem key={index} value={cell.id}>
                {cell.name}
              </MenuItem>
            ))}
          </PSelect>
        </FormControl>
      </div>
      {ffContext.groupByCellIdHandle[0] === UNGROUPED_CELL_ID ? (
        <Alert severity="info">Please group responses to see timeline</Alert>
      ) : series.length === 0 ? (
        <Alert severity="info">No {ffContext.groupByCell?.name} data</Alert>
      ) : (
        <HighchartsReact highcharts={Highcharts} options={options} />
      )}
    </Box>
  );
}
